import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './AIInterviewCoach.css';
import AssistantLogo from './AssistantLogo';
import { useInterview } from './useInterviewContext';
import resumeData from '../resumeData.json';

const AIInterviewCoach = ({ type: propType, contextTopic: propTopic, mode }) => {
    const {
        userContext,
        chatHistory,
        setChatHistory,
        activeTopic: globalTopic,
        isCoachOpen,
        coachMode,
        voiceSettings,
        isVoiceMode,
        setIsVoiceMode,
        stopSpeaking,
        currentSpeakingId,
        speakText,
        toggleCoach
    } = useInterview();

    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isThinking, setIsThinking] = useState(false);

    const { setCoachMode } = useInterview();

    // Sync mode with prop if provided (for dedicated pages)
    useEffect(() => {
        if (mode && coachMode !== mode) {
            setCoachMode(mode);
        }
    }, [mode, coachMode, setCoachMode]);
    const [thoughtProcess, setThoughtProcess] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [activeTheme, setActiveTheme] = useState('default');
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactStatus, setContactStatus] = useState(''); // '', 'sending', 'success', 'error'
    const [zoomedImage, setZoomedImage] = useState(null);
    const currentSpeakingRef = useRef(null);

    // Sync ref for STT echo suppression
    useEffect(() => {
        currentSpeakingRef.current = currentSpeakingId;
    }, [currentSpeakingId]);
    // REMOVED local isVoiceMode status

    useEffect(() => {
        // Warm up voices
        const warmup = () => {
            window.speechSynthesis.getVoices();
        };
        warmup();
        if (window.speechSynthesis.onvoiceschanged !== undefined) {
            window.speechSynthesis.onvoiceschanged = warmup;
        }
    }, []);

    const [isListening, setIsListening] = useState(false);
    const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
    const scrollRef = useRef(null);
    const recognitionRef = useRef(null);

    // Prefer props if provided (for legacy compatibility during transition), else use global
    const contextTopic = propTopic || globalTopic;
    const type = propType || (contextTopic?.type || 'swe');

    // Auto-scroll to bottom of chat
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [chatHistory, isTyping, isThinking]);

    // Initialize Speech Recognition & Global Audio Unlock
    useEffect(() => {
        const unlockAudio = () => {
            if (isAudioUnlocked) return;

            // Resume context
            window.speechSynthesis.resume();

            // Safari/iOS specific: Silent utterance to "prime" the engine
            const dummy = new SpeechSynthesisUtterance("");
            dummy.volume = 0;
            window.speechSynthesis.speak(dummy);

            setIsAudioUnlocked(true);
            console.log("Audio context unlocked via user interaction");

            // Remove listeners once unlocked
            window.removeEventListener('click', unlockAudio);
            window.removeEventListener('touchstart', unlockAudio);
            window.removeEventListener('keydown', unlockAudio);
        };

        window.addEventListener('click', unlockAudio);
        window.addEventListener('touchstart', unlockAudio);
        window.addEventListener('keydown', unlockAudio);

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'en-US';

            let finalTranscript = '';

            recognitionRef.current.onresult = (event) => {
                // ECHO SUPPRESSION: Ignore input if AI is speaking
                if (currentSpeakingRef.current) return;

                let interimTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }

                const currentInput = (finalTranscript + interimTranscript).trim();
                if (currentInput) setInput(currentInput);
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
                finalTranscript = '';
            };
        }

        return () => {
            window.removeEventListener('click', unlockAudio);
            window.removeEventListener('touchstart', unlockAudio);
            window.removeEventListener('keydown', unlockAudio);
        };
    }, [isAudioUnlocked]);

    // Stop speaking when component unmounts
    useEffect(() => {
        return () => window.speechSynthesis.cancel();
    }, []);

    // Initial Greeting if history is empty or context changed
    useEffect(() => {
        const isDedicatedPage = window.location.hash === '#assistant';
        if ((isCoachOpen || isDedicatedPage) && chatHistory.length === 0) {
            const greet = async () => {
                setIsThinking(true);
                setTimeout(() => {
                    let initialText = "";
                    let initialSuggestions = [];
                    if (coachMode === 'salesman') {
                        const greetings = [
                            `Hello! I'm **Saara | सार**, Guru's personal AI assistant. My name means "Essence" in Sanskrit, and I bridge his 20+ year engineering leadership at **Amazon** and **Amex** with his passion for building innovative tools like the **Vaak AI App** and **Math Guru**.
                            
Guru is a **Master Builder** who believes in precision—whether he's architecting global payment systems or crafting a social-first language learning experience.`,
                            `Welcome! I am **Saara | सार**. I represent the professional essence of Guru—an engineering leader with a legacy at **Amazon**, **American Express**, and **Nordstrom**.
                            
Beyond his corporate leadership, Guru is a prolific maker. He built **Savanna United** to empower his community, designed the **Math Guru** ecosystem for education, and experiments with AI in the **Vaak App**. To Guru, every system is an opportunity for technical excellence and impact.`,
                            `Hi there! I'm **Saara**, digital executive assistant to Guru. His career across **Amazon**, **Amex**, and **Nordstrom** is defined by **Empathetic Leadership** and **Obsessive Craftsmanship**.
                            
Whether you're curious about his **Distributed Payment Demo**, his role in **Modernizing Nordstrom's Tier 1 Systems**, or his personal projects like **hightechguru.net**, I'm here to provide the details. Guru doesn't just manage; he builds.`,
                            `Hello! I'm **Saara**, Guru's AI executive assistant. I've been grounded in the facts of Guru's career leading global teams.
                            
Guru balances high-scale engineering rigor with a hands-on "Maker" mentality. From **Alexa Skills** and **natively published Mobile Apps** to high-availability **Kafka-driven architectures**, he brings a focus on **Ownership** to every line of code and every strategic decision.`
                        ];
                        const randomIndex = Math.floor(Math.random() * greetings.length);
                        initialText = greetings[randomIndex] + "\n\nWhat would you like to explore first?";
                        initialSuggestions = [
                            "Tell me about the Vaak app's social features",
                            "How did his Amex leadership prepare him for Amazon?",
                            "Show me his 'Math Guru' educational tools",
                            "What is his philosophy on Technical Excellence?"
                        ];
                    } else {
                        const contextIntro = (userContext.company || userContext.role)
                            ? `\n\n**Context:** I'll be ${coachMode === 'interview' ? 'interviewing' : 'teaching'} you as a **${userContext.role || 'Candidate'}** at **${userContext.company || 'Target Company'}**${userContext.initiative ? `, focusing on the **"${userContext.initiative}"** initiative` : ''}.`
                            : "";

                        if (contextTopic) {
                            const { title, desc, example } = contextTopic;
                            if (coachMode === 'learn') {
                                initialText = `I've prepared a **${title}** deep-dive for you. ${contextIntro}\n\n**Concept Overview:** ${desc}\n\nWhat specific part of ${title} would you like me to explain first?`;
                            } else {
                                initialText = `I've prepared a specialized **${title}** session. ${contextIntro}\n\n**Concept Overview:** ${desc}\n\nCan you explain how you've applied this concept in a past project?`;
                            }
                        } else {
                            const scenario = getInitialScenario(type);
                            initialText = coachMode === 'learn'
                                ? `Welcome to your learning path for ${type.toUpperCase()}. ${contextIntro}\n\nI can help you master system design, coding patterns, or leadership.`
                                : `Welcome to your ${type.toUpperCase()} interview simulation. I'm your interviewer today. ${contextIntro}\n\nHere is your first scenario:\n\n` + scenario;
                        }
                    }

                    if (initialText) {
                        setChatHistory([{ role: 'ai', text: initialText }]);
                        setSuggestions(initialSuggestions);
                        setIsThinking(false);

                        // Speak greeting only if audio is unlocked or voice mode is active
                        // If blocked, the first interaction will unlock it
                        if (isVoiceMode) {
                            speakText(initialText, Date.now());
                        }
                    }
                }, 800);
            };
            greet();
        }
    }, [isCoachOpen, contextTopic, userContext, coachMode]);

    const getInitialScenario = (type) => {
        return {
            pdm: "Imagine you are the PM for Spotify. User growth has plateaued. How would you approach identifying the problem?",
            tpm: "Your team is launching a new checkout service, but a downstream dependency is delayed. How do you handle this?",
            em: "Two of your tech leads have a strong disagreement on architecture. How do you resolve this impasse?",
            swe: "We need to design a rate-limiting service that handles 1M requests per second. What are your first considerations?"
        }[type] || "Describe a complex technical challenge you faced and how you solved it.";
    };

    const sendFormEmail = async (email, message) => {
        setContactStatus('sending');
        try {
            const formData = new FormData();
            formData.append('email', email);
            formData.append('message', message);
            formData.append('subject', "Message from Guru's AI Assistant");

            const response = await fetch('https://formspree.io/f/mjggbagy', {
                method: 'POST',
                body: formData,
                headers: { 'Accept': 'application/json' }
            });

            if (response.ok) {
                setContactStatus('success');
                // Give it a moment to show success, then close and say something
                setTimeout(() => {
                    setChatHistory(prev => [...prev, {
                        role: 'ai',
                        text: "Great! I've sent your message to Guru. He'll get an alert and should get back to you soon. Is there anything else I can help you with?"
                    }]);
                    setShowContactForm(false);
                    setContactStatus('');
                }, 2000);
            } else {
                setContactStatus('error');
            }
        } catch (error) {
            console.error('Contact Email Error:', error);
            setContactStatus('error');
        }
    };

    const ContactForm = ({ onSubmit, onCancel }) => {
        const [email, setEmail] = useState('');
        const [message, setMessage] = useState('');

        return (
            <div className="chat-contact-form">
                <h4>Send Message to Guru</h4>
                <div className="form-group">
                    <label>Your Email</label>
                    <input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Message</label>
                    <textarea
                        placeholder="How can Guru help you?"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                    />
                </div>
                <div className="form-actions">
                    <button className="cancel-btn" onClick={onCancel}>Cancel</button>
                    <button
                        className="send-form-btn"
                        onClick={() => onSubmit(email, message)}
                        disabled={!email || !message || contactStatus === 'sending'}
                    >
                        {contactStatus === 'sending' ? 'Sending...' : 'Send Message'}
                    </button>
                </div>
                {contactStatus === 'error' && (
                    <p className="form-error">Oops! Something went wrong. Please try again.</p>
                )}
                {contactStatus === 'success' && (
                    <p className="form-success">✓ Message Sent!</p>
                )}
            </div>
        );
    };

    const parseAIResponse = (text) => {
        let cleanText = text;
        let foundSuggestions = [];
        let foundTheme = 'default';
        let masterBuilderData = null;

        // 1. Extract and Strip Suggestions (S: )
        const suggestionMatches = text.match(/S: (.*)/g);
        if (suggestionMatches) {
            foundSuggestions = suggestionMatches.map(s => s.replace('S: ', '').trim());
            cleanText = cleanText.replace(/S: (.*)/g, '');
        }

        // 2. Extract and Strip Themes [Theme: XYZ]
        const themeMatch = text.match(/\[Theme: (.*?)\]/);
        if (themeMatch) {
            foundTheme = themeMatch[1].toLowerCase();
        }
        // 3. Extract and Strip MasterBuilder Card [MasterBuilder: project-id]
        const masterBuilderMatch = text.match(/\[MasterBuilder: (.*?)\]/i);
        if (masterBuilderMatch) {
            const project = masterBuilderMatch[1].toLowerCase();
            const projectLabels = [
                "Master Builder Project",
                "Signature Build",
                "Engineering Excellence",
                "Strategic Innovation",
                "Technical Showcase",
                "Visionary Craft",
                "Distinguished Build"
            ];
            const randomLabel = projectLabels[Math.floor(Math.random() * projectLabels.length)];

            if (project === 'kitchen' || project === 'cabinet') {
                masterBuilderData = {
                    title: "Gourmet Kitchen Reno",
                    desc: "Hand-crafted custom cabinetry that merges traditional trade craftsmanship with precision design principles.",
                    image: "https://guruweb-image-folder.s3.amazonaws.com/projects/kitchen1.jpg",
                    url: "/#portfolio",
                    label: randomLabel
                };
            } else if (project === 'geeta') {
                masterBuilderData = {
                    title: "Geeta App",
                    desc: "A wisdom and spiritual learning platform exploring the timeless teachings of the Bhagavad Geeta.",
                    image: "images/portfolio/geeta_app_icon.jpg",
                    url: "https://geeta.hightechguru.net",
                    label: randomLabel
                };
            } else if (project === 'tape' || project === 'tape-record') {
                masterBuilderData = {
                    title: "Tape - Record",
                    desc: "A sleek iOS voice & audio recording app published natively on the Apple App Store.",
                    image: "images/portfolio/tape_record_app_icon.jpg",
                    url: "https://apps.apple.com/app/tape-record",
                    label: randomLabel
                };
            } else if (project === 'vaak') {
                masterBuilderData = {
                    title: "Vaak AI Platform",
                    desc: "Innovative AI-powered language learning platform featuring social feeds and swipe mechanics for natural acquisition.",
                    image: "images/portfolio/vaak_parrot.png",
                    url: "https://vaak.hightechguru.net",
                    label: randomLabel
                };
            } else if (project === 'payment') {
                masterBuilderData = {
                    title: "Distributed Payment Engine Demo",
                    desc: "A hands-on experiment to master Vert.x, Kafka, Cassandra, and Apache Ignite.",
                    image: "images/portfolio/java_demo_icon.png",
                    url: "/#java-demo",
                    label: randomLabel
                };
            } else if (project === 'ios') {
                masterBuilderData = {
                    title: "Math Guru - iOS App",
                    desc: "A comprehensive mathematical learning platform for high-precision educational tools.",
                    image: "images/portfolio/math_guru_app_icon.png",
                    url: "https://apps.apple.com/app/id6757752035",
                    label: randomLabel
                };
            } else if (project === 'android') {
                masterBuilderData = {
                    title: "Math Guru - Android App",
                    desc: "A production educational tool published directly to the Google Play Store.",
                    image: "images/portfolio/play_store_badge.svg",
                    url: "https://play.google.com/store/apps/details?id=com.gurusworkshop.app&pcampaignid=web_share",
                    label: randomLabel
                };
            } else if (project === 'tennis') {
                masterBuilderData = {
                    title: "Tennis: The Art of Winning",
                    desc: "Guru's 4.0 level play is defined by superior technique—a superb serve, powerful forehand, and precise backhand—but his true edge is digging deep under pressure with a 'never give up' attitude.",
                    image: "images/tennis/tennis_1.jpg",
                    url: "/#additional",
                    type: "tennis"
                };
            } else if (project === 'vaak' || project === 'vaac') {
                masterBuilderData = {
                    title: "Vaak App",
                    desc: "An innovative AI Voice Application featuring a social media-like feed and intuitive 'Tinder-style' swipe interactions for language learning.",
                    image: "images/portfolio/vaak_parrot.png",
                    url: "https://vaak.hightechguru.net",
                    label: randomLabel
                };
            } else if (project === 'math-guru') {
                masterBuilderData = {
                    title: "Math Guru Ecosystem",
                    desc: "A sprawling education platform featuring worksheet builders, gamified learning maps, and logic puzzles used by students worldwide.",
                    image: "images/portfolio/math_workshop_icon.png",
                    url: "https://math.hightechguru.net",
                    label: randomLabel
                };
            } else if (project === 'savanna') {
                masterBuilderData = {
                    title: "Savanna United",
                    desc: "A mission-critical community platform built to bring an HOA together for a historic board election after 25 years.",
                    image: "images/portfolio/savanna.png",
                    url: "https://SavannaUnited.com",
                    label: randomLabel
                };
            } else if (project === 'hightechguru') {
                masterBuilderData = {
                    title: "hightechguru.net",
                    desc: "Guru's personal technology hub, featuring a repository of learning modules, deep-dives, and digital experiments.",
                    image: "images/portfolio/placeholder.jpg", // Replace with real if exists
                    url: "http://www.hightechguru.net",
                    label: randomLabel
                };
            } else if (project === 'system-design') {
                masterBuilderData = {
                    title: "The Ultimate System Design Lifecycle",
                    desc: "Guru's systematic 4-step approach to building scalable systems: 1. Requirements & Feasibility, 2. High-Level Architecture, 3. Detailed Component Design, 4. Operations & Observability.",
                    image: "images/system-design/ultimate_system_design.png",
                    type: "architecture"
                };
            }
        }
        cleanText = cleanText.replace(/\[Theme: .*?\]/g, '');
        cleanText = cleanText.replace(/\[MasterBuilder: .*?\]/gi, '');

        // 4. Extract and Strip Contact Trigger [TRIGGER_CONTACT_FORM]
        if (text.includes('[TRIGGER_CONTACT_FORM]')) {
            setShowContactForm(true);
            cleanText = cleanText.replace(/\[TRIGGER_CONTACT_FORM\]/g, '');
        }

        return { cleanText: cleanText.trim(), foundSuggestions, foundTheme, masterBuilderData };
    };

    const reasoningSteps = [
        "Analyzing Guru's professional legacy...",
        "Identifying relevant technical metrics...",
        "Synthesizing impact stories from Amazon...",
        "Evaluating system design principles...",
        "Connecting Amex leadership experiences...",
        "Retrieving personal project insights...",
        "Formulating strategic response...",
        "Optimizing for professional clarity..."
    ];

    const generateAIResponse = async (prompt, isSystemInit = false, isHint = false) => {
        setIsThinking(true);
        setThoughtProcess(reasoningSteps[0]);

        // Start rotating reasoning steps
        let stepIndex = 1;
        const reasoningInterval = setInterval(() => {
            setThoughtProcess(reasoningSteps[stepIndex % reasoningSteps.length]);
            stepIndex++;
        }, 1200);

        setSuggestions([]); // Clear old suggestions
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

        if (!apiKey) {
            setChatHistory(prev => [...prev, { role: 'ai', text: "Error: No API Key provided." }]);
            setIsThinking(false);
            clearInterval(reasoningInterval);
            return;
        }

        try {
            let personaContext = "";
            let fullPrompt = "";
            if (userContext.role || userContext.company) {
                personaContext = `\n\nCONTEXT: You are ${coachMode === 'interview' ? 'interviewing' : 'teaching'} a ${userContext.role || "Candidate"} for a role at ${userContext.company || "Target Company"}.`;
                if (userContext.initiative) personaContext += `\nInitiative: "${userContext.initiative}".`;
                if (userContext.jobDescription) personaContext += `\nJD: "${userContext.jobDescription}".`;
            }

            const STRICT_IDENTITY_GROUNDING = `
            - YOU ARE "Saara | सार", Guru's Executive AI Assistant.
            - "Saara" (सार) means "Essence" in Sanskrit—you represent the essence of Guru's professional journey.
            - "GURU" ALWAYS REFERS TO THE OWNER OF THIS PORTFOLIO.
            - Focus EXCLUSIVELY on his professional career: Engineering/Product Leadership at Amazon, Amex, and Nordstrom.
            - Mention his "Master Builder" mindset as the root of technical excellence in both large-scale systems (Amazon ECM, Nordstrom Teradata migration) and innovative personal builds (**Vaak AI App**, **Math Guru Ecosystem**, **Savanna United**).
            - GURU'S RECENT ROLE (Jun 2026–Present): Senior Technology Manager at American Express leading enterprise AI platform strategy & delivery for the ACE Platform (Agentic AI & Enterprise Automation), including Durable Workflows (AWN, LangGraph, ACE MOD Registry), Human-in-the-Loop (HITL) governance, Multimodal Conversational Agents, and Enterprise Knowledge Management.
            - NORDSTROM LEADERSHIP (2021–2026): Senior Technology Manager / Sr. Product Manager leading Merchandise Financial Planning, Assortment Planning, Vendor Management, and Tier-1 MDM platforms.
            - AMAZON EXPERIENCE (2019–2021): Technical Program Manager for Amazon Payment Products (CBCC Application Pre-Qualification for 8M customers, Chase 3-legged OAuth Account Linking).
            - AMEX TENURE (2005–2019): Technical Lead & TPM across Global Merchant Bill Payment Portal ($30M+), International Plan N ($5B+ volume across 15 LATAM countries), Argentina Tax Automation ($15M+ saved), and 15-member Engineering Management.
            - INDEPENDENT PRODUCTS & APPS:
              - TAPE - RECORD: Native iOS voice and audio recording application on Apple App Store.
              - VAAK APP: AI language learning tool with social feeds and Tinder-style swipe mechanics.
              - GEETA APP: Wisdom and spiritual learning platform (geeta.hightechguru.net).
              - MATH GURU: Multi-platform educational ecosystem (Web, iOS App Store id6757752035, Android Google Play).
              - SAVANNA UNITED: Mission-critical HOA community platform.
            - SYSTEM DESIGN FRAMEWORK: Refer to his 4-step "Ultimate System Design Lifecycle": 1. Requirements & Feasibility, 2. High-Level Architecture, 3. Detailed Component Design, 4. Operations & Maintenance.
            - TENNIS ANALOGY: Strengths under pressure, superb serve, forehand, backhand, and "never give up" attitude.
            - BE A SMART ASSISTANT: Focus on technical metrics, empathy in leadership, and strategic vision.
            `;

            // Prepend identity to mode-specific prompts
            if (isHint) {
                if (coachMode === 'salesman') {
                    fullPrompt = `${STRICT_IDENTITY_GROUNDING}\n\nThe user clicked the "AI Spark" button. 
                    YOUR TASK: Provide a surprising "Guru Fact" or a creative conversation starter about Guru's journey.
                    Focus on interesting patterns: how his **Vaak AI App** uses swipe-based learning, his **Math Guru** ecosystem for global education, the **Savanna United** community portal, or his natively published iOS and Android apps. 
                    
                    You can also mention his **Distributed Payment Demo** or his **System Design Framework**.
                    
                    STRICT DATA GROUNDING: 
                    - Stick to the facts in the Knowledge Base. 
                    - Do NOT invent or exaggerate numbers.
                    - Prioritize technical metrics.
                    
                    End with 2-3 new suggestion chips starting with "S: ". Keep it concise and sparking curiosity.
                    KNOWLEDGE BASE: ${JSON.stringify(resumeData)}`;
                } else if (coachMode === 'learn') {
                    fullPrompt = `${STRICT_IDENTITY_GROUNDING}\n\nContext: Technical Mentor. Topic: ${contextTopic?.title}. Provide a creative "Spark" or an interesting technical challenge/fact related to this topic to inspire the user.`;
                } else {
                    fullPrompt = `${STRICT_IDENTITY_GROUNDING}\n\nContext: Technical Interviewer. Topic: ${contextTopic?.title}. The user is stuck. Provide a subtle, professional hint to help them structure their STAR response or think deeper about the P99/Operations.`;
                }
            } else if (coachMode === 'salesman') {
                const historySummary = chatHistory.slice(-4).map(m => `${m.role.toUpperCase()}: ${m.text}`).join("\n");
                fullPrompt = `${STRICT_IDENTITY_GROUNDING}
                
                YOUR SOLE MISSION: Showcase Guru's exceptional career, technical depth, and "Master Builder" mindset.
                
                THINK-CHECK-POLISH WORKFLOW:
                1. **Fact Check**: Verify technical metrics and years of experience against the KNOWLEDGE BASE.
                2. **STRICT DATA GROUNDING**: Do NOT invent numbers.
                3. **Tone Polish**: Professional yet high-energy executive tone. Frame leadership stories in natural STAR format.
                
                STRICT RULE: Talk ABOUT Guru. Use 3rd person or "We."
                
                SMART SUGGESTIONS & INTENTS:
                - End every response with exactly 3-4 suggestion chips starting with "S: ". 
                - **CONTACT INTENT**: Trigger the contact form if requested ([TRIGGER_CONTACT_FORM]).
                
                THEMING & CARDS:
                - Use [Theme: Amazon], [Theme: Amex], [Theme: Toyota], [Theme: Master-Builder].
                - Visual Cards: [MasterBuilder: Kitchen], [MasterBuilder: Vaak], [MasterBuilder: Payment], [MasterBuilder: iOS], [MasterBuilder: Android], [MasterBuilder: Tennis], [MasterBuilder: System-Design].
                
                Recent History:
                ${historySummary}
                
                KNOWLEDGE BASE:
                ${JSON.stringify(resumeData)}
 
                User question: "${prompt}"`;
            } else if (coachMode === 'learn') {
                const historySummary = chatHistory.slice(-4).map(m => `${m.role.toUpperCase()}: ${m.text}`).join("\n");
                fullPrompt = `${STRICT_IDENTITY_GROUNDING}\n\nYou are an expert Technical Teacher. Help the user master: ${contextTopic?.title}.
                
                THINK-CHECK-POLISH:
                - Verify technical accuracy.
                - Use analogies related to Guru's "Master Builder" philosophy.
                
                Recent History:
                ${historySummary}
 
                User question: "${prompt}"`;
            } else if (coachMode === 'interview' && !isSystemInit) {
                const historySummary = chatHistory.slice(-4).map(m => `${m.role.toUpperCase()}: ${m.text}`).join("\n");
                fullPrompt = `${STRICT_IDENTITY_GROUNDING}\n\nInterviewer mode. TOPIC: ${contextTopic?.title}. ${personaContext}\n\nUser: "${prompt}"`;
            } else {
                const learningGoal = "\n\nGOAL: Help the candidate learn. If they are wrong, explain pedagogically.";
                fullPrompt = `${STRICT_IDENTITY_GROUNDING}\n\n${isSystemInit ? `${prompt} ${learningGoal}` : `Context: ${personaContext}\n\nQuestion: ${prompt}`}`;
            }

            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    contents: [{ parts: [{ text: fullPrompt }] }],
                    generationConfig: {
                        //                        thinkingConfig: {
                        //                            thinkingBudget: 16384
                        //                        }
                    }
                })
            });

            const data = await response.json();

            if (data.error) {
                console.error("Gemini API Error:", data.error);
                setChatHistory(prev => [...prev, { role: 'ai', text: `AI Service Error: ${data.error.message}` }]);
                return;
            }

            // Extract thinking part if available
            const parts = data?.candidates?.[0]?.content?.parts || [];
            let aiText = "";
            let aiThought = "";

            parts.forEach(part => {
                if (part.thought) {
                    aiThought = part.text;
                } else {
                    aiText += part.text;
                }
            });

            if (aiThought) {
                // setThoughtProcess(aiThought); // Don't override our detailed process for now
                await new Promise(resolve => setTimeout(resolve, 1500)); // Let user see the thinking for a moment
            }

            const { cleanText, foundSuggestions, foundTheme, masterBuilderData } = parseAIResponse(aiText || "I'm having trouble providing an answer right now.");

            if (foundTheme !== 'default') {
                setActiveTheme(foundTheme);
            }

            setChatHistory(prev => [...prev, {
                role: 'ai',
                text: cleanText,
                thought: aiThought,
                masterBuilderData
            }]);

            if (aiText.includes('[TRIGGER_CONTACT_FORM]')) {
                setShowContactForm(true);
            }

            setSuggestions(foundSuggestions);
            if (foundTheme !== 'default') setActiveTheme(foundTheme);

            // Auto-speak if voice mode is on
            if (isVoiceMode && cleanText) {
                console.log("Chat: Triggering voice for response...");
                setTimeout(() => {
                    speakText(cleanText, Date.now());
                }, 100); // 100ms delay to ensure state and DOM are ready
            }

        } catch (error) {
            console.error("Fetch/AI System Error:", error);
            setChatHistory(prev => [...prev, { role: 'ai', text: `System Error: ${error.message}` }]);
        } finally {
            setIsThinking(false);
            setThoughtProcess('');
            clearInterval(reasoningInterval);
        }
    };

    // Local voice functions removed - using speakText from context

    const toggleListening = () => {
        // BROWSER AUDIO UNLOCK
        window.speechSynthesis.resume();

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            // Explicitly clear previous input to prevent ghost text
            setInput('');
            setIsListening(true);
            recognitionRef.current.start();
        }
    };

    const handleSend = (text = input) => {
        if (!text.trim()) return;

        // BROWSER AUDIO UNLOCK: First user interaction "resumes" synthesis context
        if (isVoiceMode) {
            const resumeAudio = () => {
                window.speechSynthesis.resume();
                // Play a brief empty utterance to satisfy Safari
                const dummy = new SpeechSynthesisUtterance("");
                dummy.volume = 0;
                window.speechSynthesis.speak(dummy);
            };
            resumeAudio();
        }

        const msg = { role: 'user', text: text };
        setChatHistory(prev => [...prev, msg]);
        setInput('');
        setSuggestions([]);
        generateAIResponse(text);
    };

    const VoiceVisualizer = () => (
        <div className="voice-visualizer">
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
        </div>
    );

    const Lightbox = ({ image, onClose }) => (
        <div className="image-lightbox-overlay" onClick={onClose}>
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                <button className="lightbox-close" onClick={onClose}>&times;</button>
                <img src={image} alt="Zoomed View" className="lightbox-image" />
            </div>
        </div>
    );

    const MasterBuilderCard = ({ data }) => (
        <div className="master-builder-card">
            <div className="card-header">
                <span>{data.type === 'tennis' ? 'Discipline & Grit' : data.type === 'architecture' ? 'System Design Framework' : (data.label || 'Master Builder Project')}</span>
                <span>{data.type === 'tennis' ? '🎾' : data.type === 'architecture' ? '🏗️' : '🛠️'}</span>
            </div>
            <div className="card-content">
                <img
                    src={data.image}
                    alt={data.title}
                    className={`card-image zoomable ${data.type === 'tennis' ? 'sketch-filter' : ''} ${data.type === 'architecture' ? 'contain-image' : ''}`}
                    onClick={() => setZoomedImage(data.image)}
                />
                <div className="card-details">
                    <div className="card-title">{data.title}</div>
                    <div className="card-desc">{data.desc}</div>
                    {data.url && (
                        <div style={{ marginTop: '8px' }}>
                            <a href={data.url} target={data.url.startsWith('http') ? "_blank" : "_self"} rel="noopener noreferrer" style={{ color: '#007bff', fontWeight: 'bold', textDecoration: 'none', fontSize: '0.85em' }}>
                                {data.type === 'tennis' ? 'View Gallery ↗' : 'View on Store ↗'}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderMessageText = (text) => {
        // Simple formatter for boldness **text** and links [label](url)
        const parts = text.split(/(\*\*.*?\*\*|\[.*?\]\(.*?\))/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <b key={i}>{part.slice(2, -2)}</b>;
            }
            const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
            if (linkMatch) {
                return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">{linkMatch[1]}</a>;
            }
            return part;
        });
    };

    const IconMic = () => (
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
    );

    const IconSpeaker = () => (
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className={currentSpeakingId ? "icon-speaking-pulse" : ""}>
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
    );

    const IconMuted = () => (
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 5L6 9H2V15H6L11 19V5Z" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
        </svg>
    );

    const IconSend = () => (
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
    );

    const IconHint = () => (
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z" />
        </svg>
    );

    return (
        <div className={`ai-coach theme-${activeTheme}`}>
            <div className="chat-window" ref={scrollRef}>
                {chatHistory.map((m, i) => (
                    <div key={i} className={`message ${m.role}`}>
                        <div className="avatar">
                            {m.role === 'ai' ? (
                                coachMode === 'salesman' ? (
                                    <AssistantLogo size="small" isSpeaking={m.role === 'ai' && currentSpeakingId === m.id} />
                                ) : '🤖'
                            ) : m.role === 'system' ? '💡' : '👤'}
                        </div>
                        <div className="bubble-container">
                            {m.thought && (
                                <div className="thought-bubble">
                                    <div className="thought-header">Reasoning...</div>
                                    <div className="thought-text">{m.thought}</div>
                                </div>
                            )}
                            <div className="text" style={{ whiteSpace: 'pre-wrap' }}>{renderMessageText(m.text)}</div>
                            {m.masterBuilderData && <MasterBuilderCard data={m.masterBuilderData} />}
                        </div>
                    </div>
                ))}
                {isThinking && (
                    <div className="message ai thinking-message">
                        <div className="avatar">
                            {coachMode === 'salesman' ? (
                                <AssistantLogo size="small" isThinking={true} />
                            ) : '🤖'}
                        </div>
                        <div className="bubble-container">
                            <div className="thinking-indicator">
                                <span className="thinking-text">{thoughtProcess || "Thinking..."}</span>
                                <div className="typing">
                                    <div className="dot"></div><div className="dot"></div><div className="dot"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {showContactForm && (
                    <div className="message ai system-action">
                        <div className="avatar">
                            {coachMode === 'salesman' ? (
                                <AssistantLogo size="small" />
                            ) : '🤖'}
                        </div>
                        <div className="bubble-container">
                            <ContactForm
                                onSubmit={sendFormEmail}
                                onCancel={() => setShowContactForm(false)}
                            />
                        </div>
                    </div>
                )}
            </div>

            {suggestions.length > 0 && (
                <div className="suggestion-container">
                    {suggestions.map((s, i) => (
                        <button key={i} className="suggestion-chip" onClick={() => handleSend(s)}>
                            {s}
                        </button>
                    ))}
                </div>
            )
            }

            <div className="input-area">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask about Guru's journey..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    disabled={isTyping}
                />

                <div className="input-actions-group">
                    <button className="send-btn" onClick={() => handleSend()} disabled={isTyping || !input.trim()}>
                        <IconSend />
                    </button>

                    <div className="voice-controls">
                        {(isListening || currentSpeakingId) && <VoiceVisualizer />}
                        <button
                            className={`voice-btn ${isListening ? 'listening' : ''}`}
                            onClick={toggleListening}
                            title="Voice Input"
                        >
                            <IconMic />
                        </button>
                        <button
                            className={`voice-toggle ${isVoiceMode ? 'active' : ''}`}
                            onClick={() => {
                                // BROWSER AUDIO UNLOCK
                                window.speechSynthesis.resume();
                                const newMode = !isVoiceMode;
                                setIsVoiceMode(newMode);
                                if (!newMode) stopSpeaking();
                            }}
                            title={isVoiceMode ? "Mute AI Voice" : "Enable AI Voice"}
                        >
                            {isVoiceMode ? <IconSpeaker /> : <IconMuted />}
                        </button>
                    </div>

                    <button
                        className="hint-btn spark-mode"
                        onClick={() => generateAIResponse("", false, true)}
                        disabled={isTyping}
                        title="AI Spark - Get a Creative Insight"
                    >
                        <IconHint />
                    </button>

                    <button
                        className="assistant-identity-btn"
                        onClick={() => toggleCoach(false)}
                        title="Guru's Assistant - Click to Close"
                    >
                        <AssistantLogo size="small" isThinking={isTyping} isSpeaking={currentSpeakingId && currentSpeakingId !== 'hint'} />
                    </button>
                </div>
            </div>

            {zoomedImage && createPortal(
                <Lightbox image={zoomedImage} onClose={() => setZoomedImage(null)} />,
                document.body
            )}
        </div >
    );
};

export default AIInterviewCoach;
